<template>
  <div class="faq-item">
    <!-- Question -->
    <button type="button" :id="`faq-btn-${faq._id}`" class="faq-question" :aria-expanded="isOpen"
      :aria-controls="`faq-content-${faq._id}`" @click="toggle" @focus="handleFocus"
      :style="{ background: 'none', border: 'none', width: '100%', textAlign: 'left' }">
      <h4 v-if="isNested" class="faq-question-text" :style="{ color: colors.primaryText }">{{ faq.question }}</h4>
      <h3 v-else class="faq-question-text" :style="{ color: colors.primaryText }">{{ faq.question }}</h3>
      <svg :class="{ open: isOpen }" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="20" height="20"
        viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
        stroke-linejoin="round">
        <polyline points="6 9 12 15 18 9"></polyline>
      </svg>
    </button>

    <!-- Answer -->
    <div v-show="isOpen" :id="`faq-content-${faq._id}`" class="faq-answer" :style="{ color: colors.secondaryText }"
      role="region" :aria-labelledby="`faq-btn-${faq._id}`">
      <div v-html="faq.answer"></div>
    </div>

    <!-- Hidden live region to announce answer for screen readers -->
    <div aria-live="polite" aria-atomic="true" class="sr-only">
      <div v-if="isOpen" v-html="faq.answer"></div>
    </div>

    <!-- Nested FAQs -->
    <div v-if="isOpen && faq.nested && faq.nested.length > 0" class="nested-faqs">
      <CommonFaqAccordion :isNested=true v-for="(nestedFaq, idx) in faq.nested" :key="nestedFaq._id || idx" :faq="nestedFaq"
        :fetch-children="false" />
    </div>

    <div v-if="isOpen && loadingNested" class="nested-loading" role="status" :style="{ color: colors.secondaryText }">
      Loading related FAQs...
    </div>
  </div>
</template>

<script setup>
import { ref } from "vue";
import { useTheme } from "~/composables/useTheme";
import faqService from "~/service/faqService";

const { colors } = useTheme();

const props = defineProps({
  faq: {
    type: Object,
    required: true,
    default: () => ({ question: "", answer: "", nested: [], _id: "" }),
  },
  initiallyOpen: { type: Boolean, default: false },
  fetchChildren: { type: Boolean, default: true }, // only top-level will pass true
  isNested: { type: Boolean, default: false },
});

const isOpen = ref(props.initiallyOpen);
const loadingNested = ref(false);

async function toggle() {
  isOpen.value = !isOpen.value;

  // Only fetch nested FAQs if fetchChildren is true
  if (
    isOpen.value &&
    props.fetchChildren &&
    props.faq._id &&
    props.faq.nested.length === 0
  ) {
    loadingNested.value = true;
    try {
      const response = await faqService.getFaqByEntity(props.faq._id);
      const data = response.data;

      if (data && data.length > 0) {
        props.faq.nested = data.map((item) => ({
          _id: item._id,
          question: item.title,
          answer: item.description,
          nested: [],
        }));
      }
    } catch (err) {
      console.error("Error fetching nested FAQs:", err);
    } finally {
      loadingNested.value = false;
    }
  }
}

const handleFocus = (event) => {
  // Only auto-open if the focus was initiated via keyboard (Tab)
  if (event.target.matches(":focus-visible")) {
    if (!isOpen.value) {
      toggle();
    }
  }
};
</script>

<style scoped>
.faq-item {
  margin-bottom: 10px;
  padding: 12px 0;
  width: 100%;
}

.faq-question {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  font-weight: 600;
  font-size: 16px;
}
.faq-question-text {
  font-size: 1rem;
}
.faq-question svg {
  transition: transform 0.3s ease;
}

.faq-question svg.open {
  transform: rotate(180deg);
}

.faq-answer {
  margin-top: 8px;
  font-size: 14px;
}

.nested-faqs {
  margin-left: 15px;
  margin-top: 8px;
  border-left: 2px solid #ddd;
  padding-left: 10px;
}

.nested-loading {
  margin-left: 15px;
  margin-top: 8px;
  font-size: 14px;
}

:deep(ul) {
  padding-inline-start: 20px
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}
</style>